import dayjs, { Dayjs } from "dayjs";
import { ClassType } from "shared/types";

// Snapshot of an in-progress schedule/booking flow, carried through
// react-router `state` across a round trip to a client's detail page and
// back — not persisted anywhere, so it can't go stale; if the admin
// navigates away some other way, it's simply gone. Every kind carries its
// own `returnTo` so CustomerDetails.tsx's back button can relay to whatever
// page the admin actually came from, rather than a fixed destination.

// The create-schedule drawer's full in-progress form state (see
// CreateScheduleDrawer.tsx's handleBuyPackage / resume effect).
export interface CreateScheduleResumeDraft {
  kind: "create-schedule";
  returnTo: string;
  formValues: Record<string, unknown>;
  selectedClass: ClassType | undefined;
  customerIds: string;
  // Set only when this in-memory draft was resumed from a SAVED server draft
  // (schedule_draft row): re-saving PUTs that row instead of creating a
  // duplicate, and a successful Jadwalkan deletes it. Lives INSIDE the draft
  // object (not as a sibling router-state key) because CustomerDetails.tsx
  // relays only `state.scheduleDraft` on the Kelola Paket round trip — a
  // sibling key would be dropped there. Never written into the stored server
  // payload (buildScheduleDraftPayload strips it); injected at resume time.
  savedDraftId?: string;
  // True only when the content is byte-identical to a stored draft row (the
  // drafts-drawer resume) — the panel then starts clean, so closing it again
  // without edits never prompts. A Kelola-Paket round trip leaves this unset:
  // that snapshot was built from a live form whose edits are unsaved, so the
  // panel resumes dirty and the close prompt protects them. In-flight only,
  // stripped from stored payloads like savedDraftId.
  pristine?: boolean;
}

// The "Booking Baru" / "Tambah ke Waitlist" modals on the schedule detail
// page only need to remember which customer was already picked (see
// NewBookingModal.component.tsx / AddToWaitlistModal.component.tsx).
export interface CustomerPickResumeDraft {
  kind: "new-booking" | "add-waitlist";
  returnTo: string;
  customerId: string;
}

// Deep-link from the booking-ref search boxes (Jadwal / Dashboard) straight
// to a specific booking's detail drawer, once the matching schedule
// occurrence has loaded (see ScheduleDetails.tsx / ScheduleBooking.tsx).
// returnTo isn't used by that flow today (the search always lands directly
// on the schedule page), but every draft kind carries it so CustomerDetails.tsx's
// back button keeps working if this draft is ever threaded through there.
export interface OpenBookingResumeDraft {
  kind: "open-booking";
  returnTo: string;
  bookingId: string;
  // The code belongs to a waitlist entry (live or expired): open the Waitlist
  // tab's drawer instead of the Bookings tab's.
  waitlist?: boolean;
}

export type ScheduleResumeDraft =
  | CreateScheduleResumeDraft
  | CustomerPickResumeDraft
  | OpenBookingResumeDraft;

const DAYJS_MARKER = "__isDayjs";

const isDayjs = (value: unknown): value is Dayjs =>
  !!value &&
  typeof value === "object" &&
  typeof (value as Dayjs).toISOString === "function" &&
  typeof (value as Dayjs).isValid === "function";

// react-router's `navigate(path, { state })` goes through the History API's
// structured-clone step, which can't carry class instances like dayjs
// (every date/time picker field) — convert them to a plain marker object
// first. Duck-typed rather than an explicit field allowlist since which
// fields exist is dynamic (RecurrenceSelect's per-day time pickers are
// named day_time_<code>, one per selected weekday).
const serializeValue = (value: unknown): unknown =>
  isDayjs(value) ? { [DAYJS_MARKER]: true, iso: value.toISOString() } : value;

const deserializeValue = (value: unknown): unknown =>
  value &&
  typeof value === "object" &&
  (value as Record<string, unknown>)[DAYJS_MARKER]
    ? dayjs((value as { iso: string }).iso)
    : value;

export const buildCreateScheduleResumeDraft = (
  returnTo: string,
  formValues: Record<string, unknown>,
  selectedClass: ClassType | undefined,
  customerIds: string,
  savedDraftId?: string
): CreateScheduleResumeDraft => ({
  kind: "create-schedule",
  returnTo,
  formValues: Object.fromEntries(
    Object.entries(formValues).map(([key, value]) => [
      key,
      serializeValue(value),
    ])
  ),
  selectedClass,
  customerIds,
  savedDraftId,
});

// ── Persisted drafts (the "Simpan Draft" feature) ──
// The same serialized snapshot, wrapped with a version marker and stored
// server-side (schedule_draft.payload via /v2/schedule-draft). The dayjs
// markers JSON round-trip unchanged through axios, so build/apply need no
// storage-specific variant. Staleness (deleted class, consumed package) is
// reconciled by CreateScheduleDrawer's resume effects, never trusted from here.

export interface ScheduleDraftPayloadV1 {
  v: 1;
  draft: CreateScheduleResumeDraft;
}

export const buildScheduleDraftPayload = (
  draft: CreateScheduleResumeDraft
): ScheduleDraftPayloadV1 => ({
  v: 1,
  // A stored payload must not remember which row it was loaded from — the id
  // (and the in-flight pristine flag) is re-injected at resume time from the
  // row actually clicked.
  draft: { ...draft, savedDraftId: undefined, pristine: undefined },
});

// Returns null when the payload isn't a shape this build understands
// (corrupt row, or a future version) — the caller shows an error toast
// rather than opening a half-broken panel.
export const parseScheduleDraftPayload = (
  payload: unknown
): CreateScheduleResumeDraft | null => {
  if (!payload || typeof payload !== "object") return null;
  const wrapped = payload as Partial<ScheduleDraftPayloadV1>;
  if (wrapped.v !== 1) return null;
  const draft = wrapped.draft;
  if (!draft || draft.kind !== "create-schedule" || !draft.formValues) {
    return null;
  }
  return draft;
};

export const applyCreateScheduleResumeDraft = (
  draft: CreateScheduleResumeDraft
): {
  formValues: Record<string, unknown>;
  selectedClass: ClassType | undefined;
  customerIds: string;
} => ({
  formValues: Object.fromEntries(
    Object.entries(draft.formValues).map(([key, value]) => [
      key,
      deserializeValue(value),
    ])
  ),
  selectedClass: draft.selectedClass,
  customerIds: draft.customerIds,
});

export const buildCustomerPickResumeDraft = (
  kind: "new-booking" | "add-waitlist",
  returnTo: string,
  customerId: string
): CustomerPickResumeDraft => ({ kind, returnTo, customerId });

export const buildOpenBookingResumeDraft = (
  returnTo: string,
  bookingId: string,
  waitlist = false
): OpenBookingResumeDraft => ({
  kind: "open-booking",
  returnTo,
  bookingId,
  waitlist,
});
